import React, { createContext, useContext, useState, useCallback } from 'react';

const AudioContext = createContext();

export const useAudio = () => {
    const context = useContext(AudioContext);
    if (!context) {
        throw new Error('useAudio must be used within an AudioProvider');
    }
    return context;
};

export const AudioProvider = ({ children }) => {
    const [currentTrack, setCurrentTrack] = useState(null);
    const [playlist, setPlaylist] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(-1);

    const playTrack = useCallback((track) => {
        setPlaylist([track]);
        setCurrentIndex(0);
        setCurrentTrack(track);
    }, []);

    const playPlaylist = useCallback((tracks, startIndex = 0) => {
        setPlaylist(tracks);
        setCurrentIndex(startIndex);
        setCurrentTrack(tracks[startIndex]);
    }, []);

    const nextTrack = useCallback(() => {
        if (currentIndex < playlist.length - 1) {
            const newIndex = currentIndex + 1;
            setCurrentIndex(newIndex);
            setCurrentTrack(playlist[newIndex]);
        }
    }, [currentIndex, playlist]);

    const previousTrack = useCallback(() => {
        if (currentIndex > 0) {
            const newIndex = currentIndex - 1;
            setCurrentIndex(newIndex);
            setCurrentTrack(playlist[newIndex]);
        }
    }, [currentIndex, playlist]);

    const closePlayer = useCallback(() => {
        setCurrentTrack(null);
        setPlaylist([]);
        setCurrentIndex(-1);
    }, []);

    const value = {
        currentTrack,
        playlist,
        currentIndex,
        playTrack,
        playPlaylist,
        nextTrack,
        previousTrack,
        closePlayer,
        hasNext: currentIndex < playlist.length - 1,
        hasPrevious: currentIndex > 0
    };

    return (
        <AudioContext.Provider value={value}>
            {children}
        </AudioContext.Provider>
    );
};
